import { styled } from "@mui/material";
import { useEffect, useState } from "react";
import {
  Control,
  Controller, UseFormGetValues, UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  RegisterOptions,
  FieldPath
} from "react-hook-form";
import { useKeyPress } from "../../../../../hooks/useKeyPress";
import colors from "../../../../../theme/themeConstants";
import CustomInput from "../../../CustomInput";
import { ISignUpFieldValues } from "../AuthSignUp";
import { BaseButton } from "components/buttons/BaseButton";
import Captcha from "../../../Captcha";
import { apiEmail } from "services";
import { AxiosError } from "axios";
import { IErrorData } from "services/types";

interface IProps {
  next: () => void;
  control: Control<ISignUpFieldValues>;
  trigger: UseFormTrigger<ISignUpFieldValues>;
  register: UseFormRegister<ISignUpFieldValues>;
  setValue: UseFormSetValue<ISignUpFieldValues>;
  getValues: UseFormGetValues<ISignUpFieldValues>;
}

const SignUpUsernameStep = (props: IProps) => {
  const { next, control, trigger, register, setValue, getValues } = props;

  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [disabled, setDisabled] = useState(false);
  const [resetCaptcha, setResetCaptcha] = useState<(() => void) | null>(null);

  register<FieldPath<ISignUpFieldValues>>("captcha", { required: true });

  const enterPressed = useKeyPress("Enter");

  const handleNext = async () => {
    if (await trigger(["username", "email", "captcha"])) {
      await sendCode();
    }
  };

  const sendCode = async () => {
    const { username, email, captcha, is_confirm_email } = getValues();

    if (is_confirm_email) {
      next();
      return;
    }

    setDisabled(true);
    setErrorMessage(null);

    try {
      const result = await apiEmail.sendCode({ username, email, captcha });
      setValue<FieldPath<ISignUpFieldValues>>("validation_token", result.token);
      next();
    } catch (e) {
      console.warn(e);
      const message = (e as AxiosError<IErrorData>)?.response?.data?.error?.message || "Service unavailable";
      setErrorMessage(message);
      if (resetCaptcha !== null) {
        resetCaptcha();
      }
    } finally {
      setDisabled(false);
    }
  };

  const handleCaptchaSuccess = (token: string) => {
    setValue<FieldPath<ISignUpFieldValues>>("captcha", token);
  };

  const handleCaptchaReset = () => {
    setValue<FieldPath<ISignUpFieldValues>>("captcha", "");
  };

  const handleCaptchaRendered = (reset: () => void) => {
    setValue<FieldPath<ISignUpFieldValues>>("captcha", "");
    setResetCaptcha(() => reset);
  };

  useEffect(() => {
    if (enterPressed) {
      handleNext();
    }
    // eslint-disable-next-line
  }, [enterPressed])

  return (
    <Container>
      <Controller
        name="username"
        control={control}
        defaultValue=""
        rules={{
          required: "Please come up with a user name.",
          minLength: {
            value: 6,
            message: "User name is too short."
          },
          maxLength: {
            value: 32,
            message: "User name is too long."
          },
          pattern: {
            value: /^[a-z0-9-]{6,}$/,
            message: "Please only use lower case letters and digits."
          }
        } as RegisterOptions }
        render={({ field, fieldState }) => (
          <Block>
            <label>Username</label>
            <CustomInput
              value={field.value}
              onChange={async (e) => {
                field.onChange(e);
                await trigger("username");
              }}
              error={!!fieldState.error}
              helperText={fieldState.error ? fieldState.error.message : ""}
              fullWidth
            />
          </Block>
        )}
      />
      <Controller
        name="email"
        control={control}
        defaultValue=""
        rules={{
          required: {
            value: true,
            message: "Please specify your email address."
          },
          pattern: {
            value: /^[A-Za-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}/,
            message: "Invalid Email!"
          }
        } as RegisterOptions }
        render={({ field, fieldState }) => (
          <Block>
            <label>Email</label>
            <CustomInput
              value={field.value}
              onChange={async (e) => {
                field.onChange(e);
                setValue<FieldPath<ISignUpFieldValues>>("is_confirm_email", false);
                await trigger("email");
              }}
              error={!!fieldState.error}
              helperText={fieldState.error ? fieldState.error.message : ""}
              fullWidth
            />
          </Block>
        )}
      />

      <Captcha
        id="captcha"
        onSuccess={handleCaptchaSuccess}
        onFailed={handleCaptchaReset}
        onRendered={handleCaptchaRendered}
      />

      { errorMessage &&
        <ErrorMessage>
          { errorMessage }
        </ErrorMessage>
      }

      <StyledBaseButton>
        <BaseButton type="button" onClick={handleNext} disabled={disabled}>Next</BaseButton>
      </StyledBaseButton>
    </Container>
  );
};

export default SignUpUsernameStep;

const Container = styled("div")({
  marginTop: "2rem",
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  "*": {
    fontFamily: "'Poppins', sans-serif!important",
  },
});

const Block = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  "& label": {
    fontSize: "12px",
    color: colors.textDark
  }
});

const ErrorMessage = styled("div")({
  marginTop: "1rem",
  color: "red",
  textAlign: "center",
  fontWeight: 400,
  fontSize: "0.75rem",
});

const StyledBaseButton = styled("div")({
  ".tablet &, .phone &": {
    position: "absolute",
    marginTop: 0,
    bottom: "0",
    left: "0",
    width: "100%",
    padding: "16px 16px 32px",
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "24px"
  }
});
